import * as React from 'react'
import { GoalWithProgress, Role, UserCompanyDto } from '../../services/api.types'
import { Card } from '../Card/Card'
import { t, c } from 'ttag'
import { Grid } from '@material-ui/core'
import Loader from '../Loader/Loader'
import { convertUTCToLocalDate } from '../../common/helpers/date'
import YumButton from '../YumButton/YumButton'
import { ROUTES } from '../../common/Routes/routePaths'
import YumSmallSelect from '../YumSmallSelect/YumSmallSelect'
import YumTabs from '../YumTabs/YumTabs'
import YumTab from '../YumTabs/YumTab/YumTab'
import { isPublicChallengeCompany, yuMuuvPublicCompanyId } from '../../views/Settings/Settings'
import './YumGoalList.scss'
import equal from 'fast-deep-equal'

import { isRtl } from '../../i18nInit'
import { YumGoalCard } from '../YumGoalCard/YumGoalCard'
import { PluginListenerHandle } from '@capacitor/core'
import { App } from '@capacitor/app'
import { isNative } from '../../common/helpers/isNative'

const ACTIVITY_TAB_PAST = 0
const ACTIVITY_TAB_CURRENT = 1
const ACTIVITY_TAB_FUTURE = 2

const ACTIVITY_TAB_TYPE_PRIVATE = 0
const ACTIVITY_TAB_TYPE_ORG = 1
const ACTIVITY_TAB_TYPE_PUBLIC = 2

export interface IYumGoalListProps {
  goals: GoalWithProgress[]
  loading: boolean
  activityListTab: number
  activityListTypeTab: number
  activityListTabSetAt: number
  hasAnyRole(roles: Role[]): boolean
  userCompanies: UserCompanyDto[]
}

export interface IYumGoalListDispatchProps {
  setActivityListTab(payload: number): void
  setActivityListTypeTab(payload: number): void
}

let appStateChangeListener: PluginListenerHandle

export class YumGoalList extends React.PureComponent<IYumGoalListProps & IYumGoalListDispatchProps> {
  preventEmptyCurrentView(goals: GoalWithProgress[], activityListTab: number) {
    //console.log('preventEmptyCurrentView | tab: ', activityListTab)
    // Current
    if (goals.length && activityListTab === ACTIVITY_TAB_CURRENT) {
      const currentGoals = goals.filter(
        (goal) =>
          convertUTCToLocalDate(new Date(goal.startDate)).getTime() <= new Date().getTime() &&
          convertUTCToLocalDate(new Date(goal.endDate)).getTime() >= new Date().getTime(),
      )
      const currentOrgGoals = currentGoals.filter(
        (goal) => !isPublicChallengeCompany(goal.companyId) && !goal.ownerUserId,
      )
      const currentPrivateGoals = currentGoals.filter(
        (goal) => !isPublicChallengeCompany(goal.companyId) && goal.ownerUserId,
      )
      const currentPublicGoals = currentGoals.filter((goal) => isPublicChallengeCompany(goal.companyId))
      // If public tab is active, and we have public goals, do nothing - all good.
      if (this.props.activityListTypeTab === ACTIVITY_TAB_TYPE_PUBLIC && currentPublicGoals.length) {
        return
      }
      // We have current org goals or current private goals.
      if (currentOrgGoals.length || currentPrivateGoals.length) {
        // We don't have current org goals.
        if (!currentOrgGoals.length) {
          // Set to private.
          this.setTypeTab(ACTIVITY_TAB_TYPE_PRIVATE)
        }
      }
      // We don't have current org goals or current private goals.
      else {
        const futureGoals = goals.filter(
          (goal) => convertUTCToLocalDate(new Date(goal.startDate)).getTime() > new Date().getTime(),
        )
        // We have future goals.
        if (futureGoals.length) {
          const orgGoals = futureGoals.filter((goal) => !isPublicChallengeCompany(goal.companyId) && !goal.ownerUserId)
          // We have future org goals.
          if (orgGoals.length) {
            // Set to org.
            this.setTypeTab(ACTIVITY_TAB_TYPE_ORG)
            // Set to future.
            this.setTab(ACTIVITY_TAB_FUTURE)
          }
          // We don't have future org goals.
          else {
            const privateGoals = futureGoals.filter(
              (goal) => !isPublicChallengeCompany(goal.companyId) && goal.ownerUserId,
            )
            // We have future private goals.
            if (privateGoals.length) {
              // Set to private.
              this.setTypeTab(ACTIVITY_TAB_TYPE_PRIVATE)
              // Set to future.
              this.setTab(ACTIVITY_TAB_FUTURE)
            }
            // We don't have future private goals (nor org goals).
            else {
              const publicGoals = futureGoals.filter((goal) => isPublicChallengeCompany(goal.companyId))
              // We have future public goals.
              if (publicGoals.length) {
                // Set to public.
                this.setTypeTab(ACTIVITY_TAB_TYPE_PUBLIC)
                // Set to future.
                this.setTab(ACTIVITY_TAB_FUTURE)
              }
            }
          }
        }
        // No future goals.
        else {
          // Check if we have current public goals.
          if (currentGoals.length) {
            // Set to public.
            this.setTypeTab(ACTIVITY_TAB_TYPE_PUBLIC)
          }
        }
      }
    }
  }

  preventEmptyFutureView(goals: GoalWithProgress[]) {
    // Handle issue when challenge has started, but you're still on the future tab.
    const tabLastChanged = this.props.activityListTabSetAt
    if (goals.length && this.props.activityListTab === ACTIVITY_TAB_FUTURE && tabLastChanged > 0) {
      // TODO: If all challenges are still in the future, we should keep that view open.
      const timePassed = new Date().getTime() - tabLastChanged
      console.log('timePassed', timePassed)
      // 5 minutes has passed, set back to "current".
      if (timePassed > 300000) {
        // TODO: Why set to current, if there's nothing?
        /*// Set to current.
        console.log('Setting view to CURRENT')
        this.setTab(ACTIVITY_TAB_CURRENT)*/

        // We may need to change the tab type as well.
        const currentGoals = goals.filter(
          (goal) =>
            convertUTCToLocalDate(new Date(goal.startDate)).getTime() <= new Date().getTime() &&
            convertUTCToLocalDate(new Date(goal.endDate)).getTime() >= new Date().getTime(),
        )
        const currentOrgGoals = currentGoals.filter(
          (goal) => !isPublicChallengeCompany(goal.companyId) && !goal.ownerUserId,
        )
        const currentPrivateGoals = currentGoals.filter(
          (goal) => !isPublicChallengeCompany(goal.companyId) && goal.ownerUserId,
        )
        const currentPublicGoals = currentGoals.filter((goal) => isPublicChallengeCompany(goal.companyId))

        // If we have current ORG goals, set to ORG!
        // if (this.props.activityListTypeTab !== ACTIVITY_TAB_TYPE_ORG && currentOrgGoals.length) {
        if (currentOrgGoals.length) {
          // Set to current.
          console.log('Setting view to CURRENT and tab to ORG')
          this.setTab(ACTIVITY_TAB_CURRENT)
          this.setTypeTab(ACTIVITY_TAB_TYPE_ORG)
        } else {
          if (this.props.activityListTypeTab === ACTIVITY_TAB_TYPE_PRIVATE && currentPrivateGoals.length) {
            this.setTab(ACTIVITY_TAB_CURRENT)
            // this.setTypeTab(ACTIVITY_TAB_TYPE_PRIVATE)
          } else if (this.props.activityListTypeTab === ACTIVITY_TAB_TYPE_PUBLIC && currentPublicGoals.length) {
            this.setTab(ACTIVITY_TAB_CURRENT)
            // this.setTypeTab(ACTIVITY_TAB_TYPE_PUBLIC)
          }
        }

        return ACTIVITY_TAB_CURRENT
      }
    }
    return this.props.activityListTab
  }

  preventEmptyView(goals: GoalWithProgress[]) {
    console.log('preventEmptyView')
    const activityListTab = this.preventEmptyFutureView(goals)
    this.preventEmptyCurrentView(goals, activityListTab)
  }

  componentDidMount() {
    console.log('YumGoalList | componentDidMount')
    this.preventEmptyView(this.props.goals)

    if (isNative()) {
      appStateChangeListener = App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          console.log('YumGoalList | APP RESUME | preventEmptyFutureView')
          this.preventEmptyFutureView(this.props.goals)
        }
      })
    }
  }

  componentWillUnmount() {
    if (isNative()) {
      console.log('YumGoalList | Removing appStateChangeListener')
      appStateChangeListener?.remove()
    }
  }

  componentDidUpdate(
    prevProps: Readonly<IYumGoalListProps & IYumGoalListDispatchProps>,
    prevState: Readonly<{}>,
    snapshot?: any,
  ) {
    // if (!prevProps.goals.length && this.props.goals.length) {
    if (!equal(this.props.goals, prevProps.goals)) {
      console.log('YumGoalList | componentDidUpdate | goals props has changed -> preventEmptyView')
      this.preventEmptyView(this.props.goals)
    }
  }

  setTab = (index: number) => {
    this.props.setActivityListTab(index)
  }
  setTypeTab = (index: number) => {
    // @ts-ignore
    //if (index === this.props.activityListTypeTab) index = undefined
    this.props.setActivityListTypeTab(index)
  }

  handleTabChange = (event: React.ChangeEvent<{}>, value: number) => this.setTab(value)
  handleTypeTabChange = (event: React.ChangeEvent<{}>, value: number) => this.setTypeTab(value)

  handleTabIndexChange = (index: number) => this.setTab(index)

  handlePeriodChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target as { value: string }
    this.handleTabIndexChange(parseInt(value))
  }

  renderList = (list: GoalWithProgress[], type: string) => {
    const noChallenges = type === 'current' ? t`No active challenges.` : t`No matches found.`
    return (
      <Grid className={`yum-goal-list--${type}`} container spacing={2} alignItems="stretch">
        {list.map((goal, i) => (
          <Grid className={`yum-goal-list-item`} key={goal.id} xs={12} sm={6} md={4} item>
            <YumGoalCard goal={goal} />
          </Grid>
        ))}
        {list.length === 0 && <div className="user-activity user-activity--is-empty">{noChallenges}</div>}
      </Grid>
    )
  }

  renderFilteredList = (
    goals: GoalWithProgress[],
    activeTab: number,
    activeTypeTab: number,
    publicChallengesEnabled?: boolean,
  ) => {
    let list = []

    switch (activeTypeTab) {
      // Private
      case ACTIVITY_TAB_TYPE_PRIVATE:
        list = goals.filter((goal) => !isPublicChallengeCompany(goal.companyId) && goal.ownerUserId)
        break
      // Organization
      case ACTIVITY_TAB_TYPE_ORG:
        list = goals.filter((goal) => !isPublicChallengeCompany(goal.companyId) && !goal.ownerUserId)
        break
      // Public
      case ACTIVITY_TAB_TYPE_PUBLIC:
        list = goals.filter((goal) => isPublicChallengeCompany(goal.companyId))
        break
      default:
        list = publicChallengesEnabled ? goals : goals.filter((goal) => !isPublicChallengeCompany(goal.companyId))
    }

    let type = ''
    switch (activeTab) {
      // Past
      case ACTIVITY_TAB_PAST:
        list = list.filter((goal) => convertUTCToLocalDate(new Date(goal.endDate)).getTime() < new Date().getTime())
        type = 'past'
        break
      // Current
      case ACTIVITY_TAB_CURRENT:
        list = list
          .filter(
            (goal) =>
              convertUTCToLocalDate(new Date(goal.startDate)).getTime() <= new Date().getTime() &&
              convertUTCToLocalDate(new Date(goal.endDate)).getTime() >= new Date().getTime(),
          )
          .reverse()
        type = 'current'
        break
      // Future
      case ACTIVITY_TAB_FUTURE:
        list = list
          .filter((goal) => convertUTCToLocalDate(new Date(goal.startDate)).getTime() > new Date().getTime())
          .reverse()
        type = 'future'
        break
      default:
        return '-'
    }

    const noChallenges = type === 'current' ? t`No active challenges.` : t`No matches found.`
    return (
      <Grid className={`yum-goal-list yum-goal-list--${type}`} container spacing={2} alignItems="stretch">
        {list.map((goal, i) => (
          <Grid className={`yum-goal-list-item`} key={goal.id} xs={12} sm={6} md={4} item>
            <YumGoalCard goal={goal} />
          </Grid>
        ))}
        {list.length === 0 && <div className="user-activity user-activity--is-empty">{noChallenges}</div>}
      </Grid>
    )
  }

  render() {
    const { userCompanies } = this.props
    let { goals } = this.props
    if (!goals) goals = []
    /*const pastGoals = goals.filter(
      (goal) => convertUTCToLocalDate(new Date(goal.endDate)).getTime() < new Date().getTime(),
    )
    const currentGoals = goals
      .filter(
        (goal) =>
          convertUTCToLocalDate(new Date(goal.startDate)).getTime() <= new Date().getTime() &&
          convertUTCToLocalDate(new Date(goal.endDate)).getTime() >= new Date().getTime(),
      )
      .reverse()
    const futureGoals = goals
      .filter((goal) => convertUTCToLocalDate(new Date(goal.startDate)).getTime() > new Date().getTime())
      .reverse()*/

    const userCompaniesFiltered = userCompanies.filter(({ companyId }) => companyId != yuMuuvPublicCompanyId)

    const publicChallengesAllowed = userCompanies?.some(({ publicGoalsEnabled }) => publicGoalsEnabled)
    const publicChallengesEnabled = userCompanies?.find(({ companyId }) => companyId === yuMuuvPublicCompanyId)?.active

    // const currentLabel = ngettext(msgid`Current`, msgid`Current`, currentGoals.length)
    const currentLabel = c('plural').t`Current`

    const isAdmin = this.props.hasAnyRole([Role.ADMIN])

    const userGoalsEnabled = userCompaniesFiltered.some((uc) => uc.userGoalsEnabled)
    const allowedToCreateChallenges = isAdmin || userGoalsEnabled

    const joinedPrivateGoal = userCompaniesFiltered.some((uc) => !!uc.ownerUserId)
    const joinedPublicGoal = userCompaniesFiltered.some((uc) => isPublicChallengeCompany(uc.companyId))

    const showPrivateTab = userGoalsEnabled || joinedPrivateGoal
    const showPublicTab = (publicChallengesAllowed || joinedPublicGoal) && publicChallengesEnabled

    const activeTab = this.props.activityListTab === undefined ? ACTIVITY_TAB_CURRENT : this.props.activityListTab
    let activeTypeTab =
      this.props.activityListTypeTab === undefined ? ACTIVITY_TAB_TYPE_ORG : this.props.activityListTypeTab

    if (
      (activeTypeTab === ACTIVITY_TAB_TYPE_PRIVATE && !showPrivateTab) ||
      (activeTypeTab === ACTIVITY_TAB_TYPE_PUBLIC && !showPublicTab)
    )
      activeTypeTab = ACTIVITY_TAB_TYPE_ORG

    return (
      <Card>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          style={{
            /*marginTop: -10,*/
            marginBottom: '1rem',
          }}
          wrap="nowrap"
        >
          <Grid item style={{ overflow: 'hidden' }}>
            <h1
              style={{ marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >{t`Challenges`}</h1>
          </Grid>

          <Grid item style={{ whiteSpace: 'nowrap' }}>
            <YumSmallSelect value={activeTab} onChange={this.handlePeriodChange}>
              <option value={0}>{t`Past`}</option>
              <option value={1}>{currentLabel}</option>
              <option value={2}>{t`Future`}</option>
            </YumSmallSelect>
            {allowedToCreateChallenges && (
              <YumButton
                size="small"
                color="secondary"
                to={ROUTES.CHALLENGE_ADMIN_CREATE.to}
                style={{
                  marginLeft: isRtl ? 0 : '10px',
                  marginRight: isRtl ? '10px' : 0,
                }}
              >
                {t`Create`}
                {/*<AddIcon
                    style={{
                      height: 14,
                      width: 14,
                      top: '-1px',
                      position: 'relative',
                      display: 'inline-block',
                      verticalAlign: 'middle',
                    }}
                  />*/}
              </YumButton>
            )}
          </Grid>
        </Grid>

        {this.props.loading ? (
          <Loader />
        ) : (
          <>
            {/*<Tabs
                className={'yum-goal-list__tabs'}
                value={activeTab}
                onChange={this.handleTabChange}
                variant="fullWidth"
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label={t`Past`} value={0} />
                <Tab label={currentLabel} value={1} />
                <Tab label={t`Future`} value={2} />
              </Tabs>*/}

            {showPrivateTab || showPublicTab ? (
              <YumTabs className={'yum-goal-list__tabs'} value={activeTypeTab} onChange={this.handleTypeTabChange}>
                {showPrivateTab && <YumTab label={t`Private`} value={0} />}
                <YumTab label={t`Organizational`} value={1} />
                {showPublicTab && <YumTab label={t`Public`} value={2} />}
              </YumTabs>
            ) : (
              ''
            )}

            {this.renderFilteredList(goals, activeTab, activeTypeTab, showPublicTab)}

            {/*{activeTab === 0 && this.renderList(pastGoals, 'past')}
              {activeTab === 1 && this.renderList(currentGoals, 'current')}
              {activeTab === 2 && this.renderList(futureGoals, 'future')}*/}

            {/*<SwipeableViews
            index={this.props.activityListTab}
            onChangeIndex={this.handleTabIndexChange}
            disableLazyLoading={true}
            ignoreNativeScroll={true}
            animateHeight={true}
            style={{
              margin: '0 -16px',
              minHeight: 'calc(100vh - 250px)',
            }}
          >
            {this.renderList(pastGoals, 'past')}
            {this.renderList(currentGoals, 'current')}
            {this.renderList(futureGoals, 'future')}
          </SwipeableViews>*/}
          </>
        )}
      </Card>
    )
  }
}
